import React, { useMemo, useRef, useState } from "react";
import { COLORS } from "../../theme";

// "1.15h" lido por qualquer humano parece "1h15" — mesma regra usada nas
// telas de histórico, duplicada aqui de propósito (mesmo padrão já adotado
// no resto do projeto pra esse helper, ver MeuHistorico.js/HistoricoPonto.js).
function formatarHoras(horasDecimal) {
  const valor = horasDecimal || 0;
  const totalMin = Math.round(Math.abs(valor) * 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  const sinal = valor < 0 ? "-" : valor > 0 ? "+" : "";
  return `${sinal}${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function dataCurta(dataStr) {
  return `${dataStr.slice(-2)}/${dataStr.slice(5, 7)}`;
}

// Tamanho de "detalhe" de propósito — não é o card principal da tela, é um
// traço a mais de contexto ao lado da tabela detalhada que já existe embaixo.
const W = 600;
const H = 64;
const MARGEM = { topo: 8, baixo: 8, esquerda: 6, direita: 52 };
const AREA = { largura: W - MARGEM.esquerda - MARGEM.direita, altura: H - MARGEM.topo - MARGEM.baixo };

// Linha do saldo acumulado do banco de horas ao longo do mês — acima de
// zero em verde (bom), abaixo em vermelho (precisa atenção), mesma
// convenção de cor que o resto do sistema já usa pra saldo positivo/negativo
// (Badge tone). Sem legenda: é uma série só, o título do card já diz o que é.
export default function GraficoSaldo({ dias }) {
  const svgRef = useRef(null);
  const [hoverIdx, setHoverIdx] = useState(null);

  const pontos = useMemo(
    () => (dias || []).filter((d) => d.saldo_acumulado !== null).map((d) => ({ data: d.data, valor: d.saldo_acumulado })),
    [dias]
  );

  const { linhaPath, areaPosPath, areaNegPath, baselineY, xScale, yScale } = useMemo(() => {
    if (pontos.length < 2) return {};
    const valores = pontos.map((p) => p.valor);
    let minVal = Math.min(0, ...valores);
    let maxVal = Math.max(0, ...valores);
    if (minVal === maxVal) { minVal -= 1; maxVal += 1; }
    // Uma folga de 12% em cima/embaixo pra a linha não colar no topo/base do card.
    const folga = (maxVal - minVal) * 0.12;
    minVal -= folga;
    maxVal += folga;

    const xScale = (i) => MARGEM.esquerda + (pontos.length === 1 ? 0 : (i / (pontos.length - 1)) * AREA.largura);
    const yScale = (v) => MARGEM.topo + AREA.altura * (1 - (v - minVal) / (maxVal - minVal));
    const baselineY = yScale(0);

    const coords = pontos.map((p, i) => ({ x: xScale(i), y: yScale(p.valor), valor: p.valor }));
    const linhaPath = coords.map((c, i) => `${i === 0 ? "M" : "L"} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`).join(" ");

    // Preenchimento acima/abaixo de zero: encosta o traçado na base sempre
    // que o valor real está do outro lado, então essa metade da área não
    // contribui nada ali — só a metade certa (positiva ou negativa) pinta.
    const construirArea = (clampFn) => {
      const cs = coords.map((c) => ({ x: c.x, y: yScale(clampFn(c.valor)) }));
      const primeiro = cs[0];
      const ultimo = cs[cs.length - 1];
      return [
        `M ${primeiro.x.toFixed(1)} ${baselineY.toFixed(1)}`,
        ...cs.map((c) => `L ${c.x.toFixed(1)} ${c.y.toFixed(1)}`),
        `L ${ultimo.x.toFixed(1)} ${baselineY.toFixed(1)}`,
        "Z",
      ].join(" ");
    };
    const areaPosPath = construirArea((v) => Math.max(0, v));
    const areaNegPath = construirArea((v) => Math.min(0, v));

    return { linhaPath, areaPosPath, areaNegPath, baselineY, xScale, yScale };
  }, [pontos]);

  if (pontos.length < 2) {
    return (
      <div style={{ padding: "24px 0", textAlign: "center", fontSize: 12.5, color: COLORS.textLight }}>
        Sem dias suficientes nesse mês ainda pra desenhar a tendência.
      </div>
    );
  }

  const ultimo = pontos[pontos.length - 1];
  const corUltimo = ultimo.valor > 0 ? COLORS.success : ultimo.valor < 0 ? COLORS.danger : COLORS.textLight;

  const moverPonteiro = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const xSvg = ((e.clientX - rect.left) / rect.width) * W;
    const passo = AREA.largura / (pontos.length - 1);
    const idx = Math.round((xSvg - MARGEM.esquerda) / passo);
    setHoverIdx(Math.max(0, Math.min(pontos.length - 1, idx)));
  };

  const hover = hoverIdx !== null ? { ...pontos[hoverIdx], x: xScale(hoverIdx), y: yScale(pontos[hoverIdx].valor) } : null;
  // Vira a caixinha do tooltip pra esquerda quando o ponto está na metade
  // direita do gráfico, pra nunca vazar pra fora do SVG.
  const tooltipEsquerda = hover && hover.x > W / 2;

  return (
    <div style={{ position: "relative" }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", height: "auto", display: "block", cursor: "crosshair" }}
        onMouseMove={moverPonteiro}
        onMouseLeave={() => setHoverIdx(null)}
        role="img"
        aria-label={`Saldo do banco de horas ao longo do mês, terminando em ${formatarHoras(ultimo.valor)}`}
      >
        {/* Linha de base (zero) — hairline recessiva, sempre visível, sem
            rótulo de texto (tamanho de detalhe não tem espaço pra isso) */}
        <line x1={MARGEM.esquerda} y1={baselineY} x2={W - MARGEM.direita} y2={baselineY} stroke={COLORS.border} strokeWidth="1" />

        {/* Preenchimentos diverging — verde acima, vermelho abaixo, ~10% opacidade */}
        <path d={areaPosPath} fill={COLORS.success} opacity="0.1" />
        <path d={areaNegPath} fill={COLORS.danger} opacity="0.1" />

        {/* A linha em si — cor neutra da marca, carrega o valor preciso */}
        <path d={linhaPath} fill="none" stroke={COLORS.primary} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />

        {/* Marcador + rótulo do último dia (o saldo "de hoje") */}
        <circle cx={xScale(pontos.length - 1)} cy={yScale(ultimo.valor)} r="4" fill={corUltimo} stroke={COLORS.surface} strokeWidth="1.5" />
        <text
          x={xScale(pontos.length - 1) + 7}
          y={yScale(ultimo.valor)}
          dominantBaseline="middle"
          fontSize="10.5"
          fontWeight="700"
          fill={COLORS.text}
        >
          {formatarHoras(ultimo.valor)}
        </text>

        {/* Crosshair + tooltip de hover */}
        {hover && (
          <>
            <line x1={hover.x} y1={MARGEM.topo} x2={hover.x} y2={MARGEM.topo + AREA.altura} stroke={COLORS.border} strokeWidth="1" />
            <circle cx={hover.x} cy={hover.y} r="4" fill={COLORS.primary} stroke={COLORS.surface} strokeWidth="1.5" />
            <g transform={`translate(${tooltipEsquerda ? hover.x - 92 : hover.x + 8}, 2)`}>
              <rect width="84" height="30" rx="5" fill={COLORS.text} opacity="0.92" />
              <text x="8" y="12" fontSize="9" fill={COLORS.white} opacity="0.75">{dataCurta(hover.data)}</text>
              <text x="8" y="24" fontSize="11" fontWeight="700" fill={COLORS.white}>{formatarHoras(hover.valor)}</text>
            </g>
          </>
        )}
      </svg>
    </div>
  );
}
